import { parseISO } from 'date-fns';
import type { PCADisplayType, PcaReportFilters } from 'c-sdk';
import { EsPCAReportFilterField } from 'c-sdk';
import { ReportDateTimeframe } from 'c-reports/Types';
import { cadExportFilters } from 'c-main/Components/Campaign/CampaignReportingWrapper/filteredCadExportFilters';

/**
 * UL-8220: the fields a PCA can be narrowed by. The reporting page's filter fields, less `dow`,
 * which the API refuses: the keys become Athena column names and are allow-listed against the
 * CAD export's columns (`CADReportingService::exportFilterableColumns()`), where `dow` isn't one.
 */
const PCA_FILTER_FIELDS: EsPCAReportFilterField[] = [
    EsPCAReportFilterField.DisplayOwner,
    EsPCAReportFilterField.DisplayEnv,
    EsPCAReportFilterField.DisplayFormat,
    EsPCAReportFilterField.DisplayTvRegion,
    EsPCAReportFilterField.DisplayConurbation,
    EsPCAReportFilterField.DisplayTown,
    EsPCAReportFilterField.DisplayLineName,
    EsPCAReportFilterField.CreativeName,
    EsPCAReportFilterField.FrameId,
    EsPCAReportFilterField.Schedule,
    EsPCAReportFilterField.Hour,
    EsPCAReportFilterField.IssueId,
];

const isPcaFilterField = (key: string): key is EsPCAReportFilterField =>
    (PCA_FILTER_FIELDS as string[]).includes(key);

/**
 * The PCA's starting filters: those narrowing the reporting page (as the filtered CAD export reads
 * them, so the default all-schedules selection is not a filter), limited to fields a PCA accepts.
 */
export const initialPcaFilters = (
    advancedFilters: Record<string, string[]> | null | undefined,
): PcaReportFilters =>
    Object.fromEntries(
        Object.entries(cadExportFilters(advancedFilters)).filter(([key]) => isPcaFilterField(key)),
    );

/** Append the filters to the PDF's query string as `filters[field][]=value`, as Laravel reads it. */
export const appendPcaFilterParams = (params: URLSearchParams, filters: PcaReportFilters) => {
    Object.entries(filters).forEach(([field, values]) => {
        (values ?? []).forEach(value => params.append(`filters[${field}][]`, value));
    });
    return params;
};

/**
 * The reporting page's date range, when it has one (any timeframe but "All"), for the dialog's
 * Date Range option. `yyyy-MM-dd` strings, parsed as local dates.
 */
export const initialPcaDateRange = (
    urlDate:
        | { timeframe?: ReportDateTimeframe; startDate?: string; endDate?: string }
        | null
        | undefined,
): [Date, Date] | null => {
    if (urlDate?.timeframe == null || urlDate.timeframe === ReportDateTimeframe.All) return null;
    if (!urlDate.startDate || !urlDate.endDate) return null;
    return [parseISO(urlDate.startDate), parseISO(urlDate.endDate)];
};

/**
 * Whether a PCA can carry the reporting page's filters: only a linear campaign reporting digital
 * units (`linear` and `default` are both linear). A DSP campaign, or one with classic units, gets
 * the whole-campaign PCA, unfiltered; the API refuses filters for those too.
 */
export const canFilterPca = (
    isLinear: boolean,
    displayTypes: readonly PCADisplayType[] | null | undefined,
): boolean => isLinear && !(displayTypes ?? []).includes('Classic');
